import { functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { MCCLinkStatus, CampaignMetrics } from '../types/googleAds';

/**
 * Google Ads MCC (Manager Account) Service
 * 
 * All Google Ads API calls go through Firebase Functions.
 * The MCC credentials are stored server-side for security.
 * 
 * ARCHITECTURE:
 * Frontend → Firebase Functions → Google Ads API
 *                ↑
 *           MCC Token (server-side)
 */

// ============================================================================
// TYPES
// ============================================================================

export interface MCCInvitationResult {
  success: boolean;
  resourceName?: string;
  error?: string;
}

export interface MCCStatusResult {
  status: string; // 'pending', 'connected', 'rejected'
  apiStatus: string;
  isConnected: boolean;
}

export interface LinkedAccount {
  customerId: string;
  resourceName?: string;
  name: string;
  currencyCode: string;
  timeZone: string;
  status: string;
  isLinked: boolean;
  linkedAt?: Date;
  notes?: string;
}

export interface ClientCampaign {
  id: string;
  resourceName: string;
  name: string;
  status: string;
  type: string;
  biddingStrategy: string;
  startDate?: string;
  endDate?: string;
  dailyBudget: number;
  metrics: {
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
    avgCpc: number;
    conversionValue: number;
  };
}

export interface AccountOverview {
  account: {
    customerId: string;
    name: string;
    currencyCode: string;
    timeZone: string;
  };
  metrics: CampaignMetrics & {
    conversionValue: number;
  };
  activeCampaigns: number;
  period: string;
}

export interface CampaignPerformance {
  campaignId: string;
  totals: CampaignMetrics & { conversionValue: number };
  dailyData: Array<{
    date: string;
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
    avgCpc: number;
    conversionValue: number;
  }>;
}

// ============================================================================
// MCC SERVICE
// ============================================================================

export const googleAdsMccService = {
  // ==========================================================================
  // ACCOUNT LINKING
  // ==========================================================================

  /**
   * Send an invitation to link a client account to our MCC
   */
  async sendLinkInvitation(orgId: string, customerId: string): Promise<MCCInvitationResult> {
    try {
      const sendInvite = httpsCallable<
        { orgId: string; customerId: string },
        { success: boolean; resourceName: string }
      >(functions, 'sendLinkInvitation');

      const result = await sendInvite({ orgId, customerId });
      
      return {
        success: result.data.success,
        resourceName: result.data.resourceName
      };
    } catch (error: any) {
      console.error('Error sending MCC invitation:', error);
      return {
        success: false,
        error: error.message || 'Failed to send invitation'
      };
    }
  },

  /**
   * Check the status of the link invitation
   */
  async checkLinkStatus(orgId: string): Promise<MCCStatusResult> {
    try {
      const checkStatus = httpsCallable<
        { orgId: string },
        MCCStatusResult
      >(functions, 'checkLinkStatus');

      const result = await checkStatus({ orgId });
      return result.data;
    } catch (error: any) {
      console.error('Error checking MCC link status:', error);
      throw error;
    }
  },

  // ==========================================================================
  // LINKED ACCOUNTS MANAGEMENT
  // ==========================================================================

  /**
   * List all client accounts linked to the MCC
   */
  async listLinkedAccounts(orgId: string): Promise<{
    success: boolean;
    accounts: LinkedAccount[];
    count: number;
  }> {
    try {
      const listAccounts = httpsCallable<
        { orgId: string },
        { success: boolean; accounts: LinkedAccount[]; count: number }
      >(functions, 'listLinkedAccounts');

      const result = await listAccounts({ orgId });
      return result.data;
    } catch (error: any) {
      console.error('Error listing linked accounts:', error);
      throw error;
    }
  },

  /**
   * Save a linked account to the organization
   */
  async saveLinkedAccount(
    orgId: string,
    customerId: string,
    accountName?: string,
    notes?: string
  ): Promise<{ success: boolean; account: LinkedAccount }> {
    try {
      const saveAccount = httpsCallable<
        { orgId: string; customerId: string; accountName?: string; notes?: string },
        { success: boolean; account: LinkedAccount }
      >(functions, 'saveLinkedAccount');

      const result = await saveAccount({ orgId, customerId, accountName, notes });
      return result.data;
    } catch (error: any) {
      console.error('Error saving linked account:', error);
      throw error;
    }
  },

  /**
   * Remove a linked account from the organization
   */
  async removeLinkedAccount(orgId: string, customerId: string): Promise<{ success: boolean }> {
    try {
      const removeAccount = httpsCallable<
        { orgId: string; customerId: string },
        { success: boolean }
      >(functions, 'removeLinkedAccount');

      const result = await removeAccount({ orgId, customerId });
      return result.data;
    } catch (error: any) {
      console.error('Error removing linked account:', error);
      throw error;
    }
  },

  // ==========================================================================
  // CAMPAIGN MANAGEMENT
  // ==========================================================================

  /**
   * List all campaigns for a specific client account
   */
  async listClientCampaigns(
    customerId: string,
    dateRange?: { start: string; end: string }
  ): Promise<{
    success: boolean;
    campaigns: ClientCampaign[];
    count: number;
  }> {
    try {
      const listCampaigns = httpsCallable<
        { customerId: string; dateRange?: { start: string; end: string } },
        { success: boolean; campaigns: ClientCampaign[]; count: number }
      >(functions, 'listClientCampaigns');

      const result = await listCampaigns({ customerId, dateRange });
      return result.data;
    } catch (error: any) {
      console.error('Error listing client campaigns:', error);
      throw error;
    }
  },

  /**
   * Get detailed performance for a specific campaign
   */
  async getCampaignPerformance(
    customerId: string,
    campaignId: string,
    dateRange?: { start: string; end: string }
  ): Promise<{ success: boolean } & CampaignPerformance> {
    try {
      const getPerformance = httpsCallable<
        { customerId: string; campaignId: string; dateRange?: { start: string; end: string } },
        { success: boolean } & CampaignPerformance
      >(functions, 'getCampaignPerformance');

      const result = await getPerformance({ customerId, campaignId, dateRange });
      return result.data;
    } catch (error: any) {
      console.error('Error getting campaign performance:', error);
      throw error;
    }
  },

  /**
   * Update campaign status (pause/enable)
   */
  async updateCampaignStatus(
    customerId: string,
    campaignResourceName: string,
    status: 'ENABLED' | 'PAUSED'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const updateStatus = httpsCallable<
        { customerId: string; campaignResourceName: string; status: string },
        { success: boolean; message: string }
      >(functions, 'updateCampaignStatus');

      const result = await updateStatus({ customerId, campaignResourceName, status });
      return result.data;
    } catch (error: any) {
      console.error('Error updating campaign status:', error);
      throw error;
    }
  },

  /**
   * Update campaign budget
   */
  async updateCampaignBudget(
    customerId: string,
    budgetResourceName: string,
    newDailyBudget: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const updateBudget = httpsCallable<
        { customerId: string; budgetResourceName: string; newDailyBudget: number },
        { success: boolean; message: string }
      >(functions, 'updateCampaignBudget');

      const result = await updateBudget({ customerId, budgetResourceName, newDailyBudget });
      return result.data;
    } catch (error: any) {
      console.error('Error updating campaign budget:', error);
      throw error;
    }
  },

  /**
   * Update campaign bidding strategy
   */
  async updateCampaignBiddingStrategy(
    customerId: string,
    campaignId: string,
    biddingStrategy: string,
    targetCpa?: number,
    targetRoas?: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const updateBidding = httpsCallable<
        { customerId: string; campaignId: string; biddingStrategy: string; targetCpa?: number; targetRoas?: number },
        { success: boolean; message: string }
      >(functions, 'updateCampaignBiddingStrategy');

      const result = await updateBidding({ customerId, campaignId, biddingStrategy, targetCpa, targetRoas });
      return result.data;
    } catch (error: any) {
      console.error('Error updating bidding strategy:', error);
      throw error;
    }
  },

  // ==========================================================================
  // ACCOUNT OVERVIEW
  // ==========================================================================

  /**
   * Get account overview with aggregated metrics
   */
  async getAccountOverview(customerId: string): Promise<{
    success: boolean;
  } & AccountOverview> {
    try {
      const getOverview = httpsCallable<
        { customerId: string },
        { success: boolean } & AccountOverview
      >(functions, 'getAccountOverview');

      const result = await getOverview({ customerId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting account overview:', error);
      throw error;
    }
  },

  // ==========================================================================
  // CAMPAIGN IMPORT
  // ==========================================================================

  /**
   * Import campaigns from a linked Google Ads account
   */
  async importCampaigns(orgId: string, customerId: string): Promise<{
    success: boolean;
    count: number;
    campaigns: any[];
    error?: string;
  }> {
    try {
      const importCamps = httpsCallable<
        { orgId: string; customerId: string },
        { success: boolean; count: number; campaigns: any[] }
      >(functions, 'importCampaigns');

      const result = await importCamps({ orgId, customerId });
      return result.data;
    } catch (error: any) {
      console.error('Error importing campaigns:', error);
      return {
        success: false,
        count: 0,
        campaigns: [],
        error: error.message || 'Failed to import campaigns'
      };
    }
  },

  // ==========================================================================
  // KEYWORDS & ADS
  // ==========================================================================

  /**
   * Get keywords for a specific campaign
   */
  async getCampaignKeywords(customerId: string, campaignId: string): Promise<{
    success: boolean;
    keywords: CampaignKeyword[];
    count: number;
  }> {
    try {
      const getKeywords = httpsCallable<
        { customerId: string; campaignId: string },
        { success: boolean; keywords: CampaignKeyword[]; count: number }
      >(functions, 'getCampaignKeywords');

      const result = await getKeywords({ customerId, campaignId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting campaign keywords:', error);
      throw error;
    }
  },

  /**
   * Get ads for a specific campaign
   */
  async getCampaignAds(customerId: string, campaignId: string): Promise<{
    success: boolean;
    ads: CampaignAd[];
    count: number;
  }> {
    try {
      const getAds = httpsCallable<
        { customerId: string; campaignId: string },
        { success: boolean; ads: CampaignAd[]; count: number }
      >(functions, 'getCampaignAds');

      const result = await getAds({ customerId, campaignId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting campaign ads:', error);
      throw error;
    }
  },

  // ==========================================================================
  // AD GROUPS
  // ==========================================================================

  /**
   * Get ad groups for a campaign
   */
  async getCampaignAdGroups(customerId: string, campaignId: string): Promise<{
    success: boolean;
    adGroups: AdGroup[];
    count: number;
  }> {
    try {
      const getAdGroups = httpsCallable<
        { customerId: string; campaignId: string },
        { success: boolean; adGroups: AdGroup[]; count: number }
      >(functions, 'getCampaignAdGroups');

      const result = await getAdGroups({ customerId, campaignId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting ad groups:', error);
      throw error;
    }
  },

  // ==========================================================================
  // KEYWORD MANAGEMENT
  // ==========================================================================

  /**
   * Add a single keyword to an ad group
   */
  async addKeyword(
    customerId: string,
    adGroupId: string,
    keyword: string,
    matchType: 'BROAD' | 'PHRASE' | 'EXACT' = 'BROAD',
    cpcBidMicros?: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const add = httpsCallable<
        { customerId: string; adGroupId: string; keyword: string; matchType: string; cpcBidMicros?: number },
        { success: boolean; message: string }
      >(functions, 'addKeyword');

      const result = await add({ customerId, adGroupId, keyword, matchType, cpcBidMicros });
      return result.data;
    } catch (error: any) {
      console.error('Error adding keyword:', error);
      throw error;
    }
  },

  /**
   * Add multiple keywords at once
   */
  async addKeywordsBatch(
    customerId: string,
    adGroupId: string,
    keywords: Array<{ text: string; matchType?: string; cpcBidMicros?: number }>
  ): Promise<{ success: boolean; message: string; count: number }> {
    try {
      const addBatch = httpsCallable<
        { customerId: string; adGroupId: string; keywords: Array<{ text: string; matchType?: string; cpcBidMicros?: number }> },
        { success: boolean; message: string; count: number }
      >(functions, 'addKeywordsBatch');

      const result = await addBatch({ customerId, adGroupId, keywords });
      return result.data;
    } catch (error: any) {
      console.error('Error adding keywords batch:', error);
      throw error;
    }
  },

  /**
   * Update keyword status
   */
  async updateKeywordStatus(
    customerId: string,
    keywordResourceName: string,
    status: 'ENABLED' | 'PAUSED' | 'REMOVED'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const update = httpsCallable<
        { customerId: string; keywordResourceName: string; status: string },
        { success: boolean; message: string }
      >(functions, 'updateKeywordStatus');

      const result = await update({ customerId, keywordResourceName, status });
      return result.data;
    } catch (error: any) {
      console.error('Error updating keyword status:', error);
      throw error;
    }
  },

  /**
   * Update keyword CPC bid
   */
  async updateKeywordBid(
    customerId: string,
    keywordResourceName: string,
    cpcBidMicros: number
  ): Promise<{ success: boolean; message: string }> {
    try {
      const update = httpsCallable<
        { customerId: string; keywordResourceName: string; cpcBidMicros: number },
        { success: boolean; message: string }
      >(functions, 'updateKeywordBid');

      const result = await update({ customerId, keywordResourceName, cpcBidMicros });
      return result.data;
    } catch (error: any) {
      console.error('Error updating keyword bid:', error);
      throw error;
    }
  },

  /**
   * Remove a keyword
   */
  async removeKeyword(
    customerId: string,
    keywordResourceName: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const remove = httpsCallable<
        { customerId: string; keywordResourceName: string },
        { success: boolean; message: string }
      >(functions, 'removeKeyword');

      const result = await remove({ customerId, keywordResourceName });
      return result.data;
    } catch (error: any) {
      console.error('Error removing keyword:', error);
      throw error;
    }
  },

  // ==========================================================================
  // NEGATIVE KEYWORDS
  // ==========================================================================

  /**
   * Get negative keywords for a campaign
   */
  async getCampaignNegativeKeywords(customerId: string, campaignId: string): Promise<{
    success: boolean;
    negativeKeywords: NegativeKeyword[];
    count: number;
    campaignLevel: number;
    adGroupLevel: number;
  }> {
    try {
      const getNegatives = httpsCallable<
        { customerId: string; campaignId: string },
        { success: boolean; negativeKeywords: NegativeKeyword[]; count: number; campaignLevel: number; adGroupLevel: number }
      >(functions, 'getCampaignNegativeKeywords');

      const result = await getNegatives({ customerId, campaignId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting negative keywords:', error);
      throw error;
    }
  },

  /**
   * Add negative keyword to campaign
   */
  async addCampaignNegativeKeyword(
    customerId: string,
    campaignId: string,
    keyword: string,
    matchType: 'BROAD' | 'PHRASE' | 'EXACT' = 'BROAD'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const add = httpsCallable<
        { customerId: string; campaignId: string; keyword: string; matchType: string },
        { success: boolean; message: string }
      >(functions, 'addCampaignNegativeKeyword');

      const result = await add({ customerId, campaignId, keyword, matchType });
      return result.data;
    } catch (error: any) {
      console.error('Error adding campaign negative keyword:', error);
      throw error;
    }
  },

  /**
   * Add multiple negative keywords to campaign
   */
  async addCampaignNegativeKeywordsBatch(
    customerId: string,
    campaignId: string,
    keywords: Array<{ text: string; matchType?: string }>
  ): Promise<{ success: boolean; message: string; count: number }> {
    try {
      const addBatch = httpsCallable<
        { customerId: string; campaignId: string; keywords: Array<{ text: string; matchType?: string }> },
        { success: boolean; message: string; count: number }
      >(functions, 'addCampaignNegativeKeywordsBatch');

      const result = await addBatch({ customerId, campaignId, keywords });
      return result.data;
    } catch (error: any) {
      console.error('Error adding negative keywords batch:', error);
      throw error;
    }
  },

  /**
   * Add negative keyword to ad group
   */
  async addAdGroupNegativeKeyword(
    customerId: string,
    adGroupId: string,
    keyword: string,
    matchType: 'BROAD' | 'PHRASE' | 'EXACT' = 'BROAD'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const add = httpsCallable<
        { customerId: string; adGroupId: string; keyword: string; matchType: string },
        { success: boolean; message: string }
      >(functions, 'addAdGroupNegativeKeyword');

      const result = await add({ customerId, adGroupId, keyword, matchType });
      return result.data;
    } catch (error: any) {
      console.error('Error adding ad group negative keyword:', error);
      throw error;
    }
  },

  /**
   * Remove a negative keyword
   */
  async removeNegativeKeyword(
    customerId: string,
    resourceName: string,
    level: 'CAMPAIGN' | 'AD_GROUP'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const remove = httpsCallable<
        { customerId: string; resourceName: string; level: string },
        { success: boolean; message: string }
      >(functions, 'removeNegativeKeyword');

      const result = await remove({ customerId, resourceName, level });
      return result.data;
    } catch (error: any) {
      console.error('Error removing negative keyword:', error);
      throw error;
    }
  },

  // ==========================================================================
  // AI RECOMMENDATIONS (Gemini)
  // ==========================================================================

  /**
   * Get AI-powered recommendations for a campaign using Google Gemini
   */
  async getAIRecommendations(
    campaignData: CampaignDataForAI,
    language: 'de' | 'en' = 'de'
  ): Promise<{
    success: boolean;
    recommendations: AIRecommendation[];
    count: number;
    analyzedAt: string;
    model: string;
  }> {
    try {
      const getRecommendations = httpsCallable<
        { campaignData: CampaignDataForAI; language: string },
        { success: boolean; recommendations: AIRecommendation[]; count: number; analyzedAt: string; model: string }
      >(functions, 'getAIRecommendations');

      const result = await getRecommendations({ campaignData, language });
      return result.data;
    } catch (error: any) {
      console.error('Error getting AI recommendations:', error);
      throw error;
    }
  },

  /**
   * Get AI recommendations for multiple campaigns (account-wide analysis)
   */
  async getAIRecommendationsBatch(
    campaigns: CampaignDataForAI[],
    language: 'de' | 'en' = 'de'
  ): Promise<{
    success: boolean;
    recommendations: AIRecommendation[];
    count: number;
    accountMetrics: {
      impressions: number;
      clicks: number;
      conversions: number;
      cost: number;
      ctr: number;
      conversionRate: number;
      cpa: number;
      roas: number;
    };
    analyzedAt: string;
  }> {
    try {
      const getRecommendations = httpsCallable<
        { campaigns: CampaignDataForAI[]; language: string },
        { success: boolean; recommendations: AIRecommendation[]; count: number; accountMetrics: any; analyzedAt: string }
      >(functions, 'getAIRecommendationsBatch');

      const result = await getRecommendations({ campaigns, language });
      return result.data;
    } catch (error: any) {
      console.error('Error getting batch AI recommendations:', error);
      throw error;
    }
  },

  // ==========================================================================
  // CAMPAIGN ASSETS (Extensions)
  // ==========================================================================

  /**
   * Get campaign assets (sitelinks, callouts, structured snippets, etc.)
   */
  async getCampaignAssets(
    customerId: string,
    campaignId: string
  ): Promise<CampaignAssets> {
    try {
      const getAssets = httpsCallable<
        { customerId: string; campaignId: string },
        { success: boolean; assets: CampaignAssets; count: number }
      >(functions, 'getCampaignAssets');

      const result = await getAssets({ customerId, campaignId });
      return result.data.assets;
    } catch (error: any) {
      console.error('Error fetching campaign assets:', error);
      // Return empty assets on error
      return {
        sitelinks: [],
        callouts: [],
        structuredSnippets: [],
        callAssets: [],
        all: []
      };
    }
  },

  // ==========================================================================
  // RESPONSIVE SEARCH AD MANAGEMENT
  // ==========================================================================

  /**
   * Update a Responsive Search Ad (headlines, descriptions, final URLs)
   */
  async updateResponsiveSearchAd(
    customerId: string,
    adGroupId: string,
    adId: string,
    headlines: string[],
    descriptions: string[],
    finalUrls: string[]
  ): Promise<{ success: boolean; message: string }> {
    try {
      const updateAd = httpsCallable<
        { customerId: string; adGroupId: string; adId: string; headlines: string[]; descriptions: string[]; finalUrls: string[] },
        { success: boolean; message: string }
      >(functions, 'updateResponsiveSearchAd');

      const result = await updateAd({
        customerId,
        adGroupId,
        adId,
        headlines,
        descriptions,
        finalUrls
      });

      return result.data;
    } catch (error: any) {
      console.error('Error updating RSA:', error);
      throw error;
    }
  },

  // ==========================================================================
  // ASSET MANAGEMENT (Sitelinks, Callouts, etc.)
  // ==========================================================================

  /**
   * Create a new asset
   */
  async createAsset(
    customerId: string,
    assetType: 'SITELINK' | 'CALLOUT' | 'STRUCTURED_SNIPPET' | 'CALL',
    assetData: SitelinkData | CalloutData | StructuredSnippetData | CallData
  ): Promise<{ success: boolean; resourceName: string; message: string }> {
    try {
      const create = httpsCallable<
        { customerId: string; assetType: string; assetData: any },
        { success: boolean; resourceName: string; message: string }
      >(functions, 'createAsset');

      const result = await create({ customerId, assetType, assetData });
      return result.data;
    } catch (error: any) {
      console.error('Error creating asset:', error);
      throw error;
    }
  },

  /**
   * Update an existing asset
   */
  async updateAsset(
    customerId: string,
    assetResourceName: string,
    assetType: 'SITELINK' | 'CALLOUT' | 'STRUCTURED_SNIPPET' | 'CALL',
    assetData: SitelinkData | CalloutData | StructuredSnippetData | CallData
  ): Promise<{ success: boolean; message: string }> {
    try {
      const update = httpsCallable<
        { customerId: string; assetResourceName: string; assetType: string; assetData: any },
        { success: boolean; message: string }
      >(functions, 'updateAsset');

      const result = await update({ customerId, assetResourceName, assetType, assetData });
      return result.data;
    } catch (error: any) {
      console.error('Error updating asset:', error);
      throw error;
    }
  },

  /**
   * Delete an asset
   */
  async deleteAsset(
    customerId: string,
    assetResourceName: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const remove = httpsCallable<
        { customerId: string; assetResourceName: string },
        { success: boolean; message: string }
      >(functions, 'deleteAsset');

      const result = await remove({ customerId, assetResourceName });
      return result.data;
    } catch (error: any) {
      console.error('Error deleting asset:', error);
      throw error;
    }
  },

  /**
   * Link an asset to a campaign
   */
  async linkAssetToCampaign(
    customerId: string,
    campaignId: string,
    assetResourceName: string,
    fieldType: 'SITELINK' | 'CALLOUT' | 'STRUCTURED_SNIPPET' | 'CALL'
  ): Promise<{ success: boolean; message: string }> {
    try {
      const link = httpsCallable<
        { customerId: string; campaignId: string; assetResourceName: string; fieldType: string },
        { success: boolean; message: string }
      >(functions, 'linkAssetToCampaign');

      const result = await link({ customerId, campaignId, assetResourceName, fieldType });
      return result.data;
    } catch (error: any) {
      console.error('Error linking asset:', error);
      throw error;
    }
  },

  /**
   * Unlink an asset from a campaign
   */
  async unlinkAssetFromCampaign(
    customerId: string,
    campaignAssetResourceName: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const unlink = httpsCallable<
        { customerId: string; campaignAssetResourceName: string },
        { success: boolean; message: string }
      >(functions, 'unlinkAssetFromCampaign');

      const result = await unlink({ customerId, campaignAssetResourceName });
      return result.data;
    } catch (error: any) {
      console.error('Error unlinking asset:', error);
      throw error;
    }
  },

  /**
   * Create an asset and immediately link it to a campaign
   */
  async createAndLinkAsset(
    customerId: string,
    campaignId: string,
    assetType: 'SITELINK' | 'CALLOUT' | 'STRUCTURED_SNIPPET' | 'CALL',
    assetData: SitelinkData | CalloutData | StructuredSnippetData | CallData
  ): Promise<{ success: boolean; assetResourceName: string; message: string }> {
    try {
      const createAndLink = httpsCallable<
        { customerId: string; campaignId: string; assetType: string; assetData: any },
        { success: boolean; assetResourceName: string; message: string }
      >(functions, 'createAndLinkAsset');

      const result = await createAndLink({ customerId, campaignId, assetType, assetData });
      return result.data;
    } catch (error: any) {
      console.error('Error creating and linking asset:', error);
      throw error;
    }
  },

  // ==========================================================================
  // KEYWORD PLANNER API
  // ==========================================================================

  /**
   * Generate keyword ideas using Google Ads Keyword Planner
   * Uses KeywordPlanIdeaService v22
   */
  async generateKeywordIdeas(
    customerId: string,
    options: {
      seedKeywords?: string[];
      landingPageUrl?: string;
      language?: string;
      locations?: string[];
    }
  ): Promise<{
    success: boolean;
    keywords: KeywordPlannerIdea[];
    count: number;
    language: string;
    locations: string[];
  }> {
    try {
      const generate = httpsCallable<
        { customerId: string; seedKeywords?: string[]; landingPageUrl?: string; language?: string; locations?: string[] },
        { success: boolean; keywords: KeywordPlannerIdea[]; count: number; language: string; locations: string[] }
      >(functions, 'generateKeywordIdeas');

      const result = await generate({
        customerId,
        seedKeywords: options.seedKeywords,
        landingPageUrl: options.landingPageUrl,
        language: options.language || 'German',
        locations: options.locations || ['Germany']
      });
      
      return result.data;
    } catch (error: any) {
      console.error('Error generating keyword ideas:', error);
      throw error;
    }
  },

  /**
   * Get keyword forecasts (estimated clicks, impressions, cost)
   */
  async getKeywordForecasts(
    customerId: string,
    keywords: string[],
    options?: {
      cpcBidMicros?: number;
      locations?: string[];
    }
  ): Promise<{
    success: boolean;
    forecasts: any;
    keywordForecasts: any[];
  }> {
    try {
      const forecast = httpsCallable<
        { customerId: string; keywords: string[]; cpcBidMicros?: number; locations?: string[] },
        { success: boolean; forecasts: any; keywordForecasts: any[] }
      >(functions, 'getKeywordForecasts');

      const result = await forecast({
        customerId,
        keywords,
        cpcBidMicros: options?.cpcBidMicros,
        locations: options?.locations
      });
      
      return result.data;
    } catch (error: any) {
      console.error('Error getting keyword forecasts:', error);
      throw error;
    }
  },

  // ==========================================================================
  // CAMPAIGN CREATION VIA MCC
  // ==========================================================================

  /**
   * Create a complete Search Campaign via MCC Cloud Function
   * This uses server-side MCC credentials that don't expire
   */
  async createSearchCampaign(
    customerId: string,
    campaignData: {
      campaignName: string;
      dailyBudget: number;
      startDate: Date;
      endDate?: Date;
      biddingStrategy: string;
      targetCpa?: number;
      targetRoas?: number;
      maxCpcBidLimit?: number;
      locations: string[];
      languages: string[];
      keywords: Array<{ text: string; matchType: string; maxCpc?: number }>;
      headlines: string[];
      descriptions: string[];
      finalUrl: string;
      path1?: string;
      path2?: string;
    }
  ): Promise<{
    success: boolean;
    campaignResourceName: string;
    budgetResourceName: string;
    adGroupResourceName: string;
    adResourceName?: string;
    keywordCount: number;
    message: string;
  }> {
    try {
      const createCampaign = httpsCallable<any, any>(functions, 'createSearchCampaignMcc');
      
      const result = await createCampaign({
        customerId,
        campaignName: campaignData.campaignName,
        dailyBudget: campaignData.dailyBudget,
        startDate: campaignData.startDate.toISOString(),
        endDate: campaignData.endDate?.toISOString(),
        biddingStrategy: campaignData.biddingStrategy,
        targetCpa: campaignData.targetCpa,
        targetRoas: campaignData.targetRoas,
        maxCpcBidLimit: campaignData.maxCpcBidLimit,
        locations: campaignData.locations,
        languages: campaignData.languages,
        keywords: campaignData.keywords,
        headlines: campaignData.headlines,
        descriptions: campaignData.descriptions,
        finalUrl: campaignData.finalUrl,
        path1: campaignData.path1,
        path2: campaignData.path2
      });

      console.log('✅ Campaign created via MCC:', result.data);
      return result.data;
    } catch (error: any) {
      console.error('Error creating campaign via MCC:', error);
      throw error;
    }
  },

  // ==========================================================================
  // OPTIMIZATION SCORE & RECOMMENDATIONS
  // ==========================================================================

  /**
   * Get the Optimization Score for a Google Ads customer account
   */
  async getOptimizationScore(
    customerId: string,
    orgId?: string
  ): Promise<{
    success: boolean;
    score: number | null;
    weight: number | null;
    fetchedAt: string;
  }> {
    try {
      const getScore = httpsCallable<
        { customerId: string; orgId?: string },
        { success: boolean; score: number | null; weight: number | null; fetchedAt: string }
      >(functions, 'getOptimizationScore');

      const result = await getScore({ customerId, orgId });
      return result.data;
    } catch (error: any) {
      console.error('Error getting optimization score:', error);
      throw error;
    }
  },

  /**
   * Get all active recommendations from Google Ads
   */
  async getGoogleRecommendations(
    customerId: string,
    options?: {
      orgId?: string;
      includeTypes?: string[];
    }
  ): Promise<{
    success: boolean;
    recommendations: GoogleAdsRecommendationResult[];
    count: number;
    fetchedAt: string;
  }> {
    try {
      const getRecs = httpsCallable<
        { customerId: string; orgId?: string; includeTypes?: string[] },
        { success: boolean; recommendations: GoogleAdsRecommendationResult[]; count: number; fetchedAt: string }
      >(functions, 'getGoogleRecommendations');

      const result = await getRecs({
        customerId,
        orgId: options?.orgId,
        includeTypes: options?.includeTypes
      });
      return result.data;
    } catch (error: any) {
      console.error('Error getting Google recommendations:', error);
      throw error;
    }
  },

  /**
   * Apply a Google Ads recommendation
   */
  async applyGoogleRecommendation(
    customerId: string,
    recommendationResourceName: string,
    options?: {
      orgId?: string;
      applyParameters?: {
        budgetAmountMicros?: string;
        targetCpaMicros?: string;
        targetRoas?: number;
      };
      historyData?: any;
    }
  ): Promise<{
    success: boolean;
    recommendationResourceName: string;
    appliedAt: string;
  }> {
    try {
      const apply = httpsCallable<any, any>(functions, 'applyGoogleRecommendation');

      const result = await apply({
        customerId,
        recommendationResourceName,
        orgId: options?.orgId,
        applyParameters: options?.applyParameters,
        historyData: options?.historyData
      });
      return result.data;
    } catch (error: any) {
      console.error('Error applying recommendation:', error);
      throw error;
    }
  },

  /**
   * Dismiss a Google Ads recommendation
   */
  async dismissGoogleRecommendation(
    customerId: string,
    recommendationResourceName: string,
    options?: {
      orgId?: string;
      dismissalReason?: string;
      dismissalCategory?: string;
      historyData?: any;
    }
  ): Promise<{
    success: boolean;
    recommendationResourceName: string;
    dismissedAt: string;
  }> {
    try {
      const dismiss = httpsCallable<any, any>(functions, 'dismissGoogleRecommendation');

      const result = await dismiss({
        customerId,
        recommendationResourceName,
        orgId: options?.orgId,
        dismissalReason: options?.dismissalReason,
        dismissalCategory: options?.dismissalCategory,
        historyData: options?.historyData
      });
      return result.data;
    } catch (error: any) {
      console.error('Error dismissing recommendation:', error);
      throw error;
    }
  },

  /**
   * Bulk apply multiple recommendations at once
   */
  async bulkApplyGoogleRecommendations(
    customerId: string,
    recommendations: Array<{
      resourceName: string;
      type: string;
      title?: string;
      description?: string;
      expectedImpact?: any;
    }>,
    orgId?: string
  ): Promise<{
    success: boolean;
    appliedCount: number;
    appliedAt: string;
  }> {
    try {
      const bulkApply = httpsCallable<any, any>(functions, 'bulkApplyGoogleRecommendations');

      const result = await bulkApply({
        customerId,
        recommendations,
        orgId
      });
      return result.data;
    } catch (error: any) {
      console.error('Error bulk applying recommendations:', error);
      throw error;
    }
  },

  /**
   * Get recommendation history for reporting
   */
  async getRecommendationHistory(
    orgId: string,
    options?: {
      customerId?: string;
      status?: 'applied' | 'dismissed' | 'expired';
      type?: string;
      limit?: number;
    }
  ): Promise<{
    success: boolean;
    history: GoogleRecommendationHistoryItem[];
    stats: {
      total: number;
      applied: number;
      dismissed: number;
      expired: number;
      byType: Record<string, number>;
      byDecider: Record<string, number>;
    };
    count: number;
  }> {
    try {
      const getHistory = httpsCallable<any, any>(functions, 'getRecommendationHistory');

      const result = await getHistory({
        orgId,
        customerId: options?.customerId,
        status: options?.status,
        type: options?.type,
        limit: options?.limit
      });
      return result.data;
    } catch (error: any) {
      console.error('Error getting recommendation history:', error);
      throw error;
    }
  }
};

// Types for Google Ads Recommendations (from API)
export interface GoogleAdsRecommendationResult {
  resourceName: string;
  type: string;
  campaignResourceName?: string;
  campaignName?: string;
  dismissed: boolean;
  impact: {
    baseMetrics?: any;
    potentialMetrics?: any;
    impactEstimate: string;
  };
  keywordRecommendation?: {
    keyword: { text: string; matchType: string };
    recommendedCpcBidMicros?: string;
    campaignResourceName?: string;
    adGroupResourceName?: string;
  };
  budgetRecommendation?: {
    currentBudgetAmountMicros: string;
    recommendedBudgetAmountMicros: string;
    budgetResourceName: string;
  };
  biddingRecommendation?: {
    campaignResourceName: string;
    targetCpaMicros?: string;
    targetRoas?: number;
    strategy?: string;
  };
  sitelinkRecommendation?: {
    recommendedSitelinks: Array<{
      text: string;
      finalUrl: string;
      description1?: string;
      description2?: string;
    }>;
  };
  rsaAssetRecommendation?: {
    adGroupResourceName: string;
    recommendedAssets: {
      headlines?: string[];
      descriptions?: string[];
    };
  };
  rawDetails?: any;
  fetchedAt: string;
  // AI evaluation (added by frontend)
  aiEvaluation?: {
    approved: boolean;
    confidence: number;
    reason: string;
    suggestedAction: 'apply' | 'review' | 'dismiss';
  };
}

export interface GoogleRecommendationHistoryItem {
  id: string;
  customerId: string;
  recommendationResourceName: string;
  type: string;
  title: string;
  description: string;
  expectedImpact: {
    metric: string;
    estimatedChange: string;
    baseValue?: number;
    potentialValue?: number;
  };
  status: 'applied' | 'dismissed' | 'expired' | 'pending';
  decidedAt: Date;
  decidedBy: 'ai_agent' | 'user' | 'scheduler';
  userId?: string;
  aiEvaluation?: {
    approved: boolean;
    confidence: number;
    reason: string;
    evaluatedAt: Date;
  };
  dismissalReason?: string;
  dismissalCategory?: string;
  appliedDetails?: {
    appliedAt: Date;
    changesApplied: any;
    rollbackAvailable: boolean;
  };
  postApplyMetrics?: {
    measuredAt: Date;
    actualImpact: string;
    metExpectations: boolean;
  };
  fetchedAt: Date;
  campaignId?: string;
  campaignName?: string;
  orgId: string;
}

// Types for keywords and ads
export interface CampaignKeyword {
  id: string;
  resourceName: string;
  text: string;
  matchType: string;
  status: string;
  adGroupId: string;
  adGroupName: string;
  metrics: {
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
    avgCpc: number;
  };
}

export interface CampaignAd {
  id: string;
  name: string;
  resourceName: string;
  finalUrls: string[];
  status: string;
  adGroupId: string;
  adGroupName: string;
  headlines: string[];
  descriptions: string[];
  metrics: {
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
  };
}

export interface AdGroup {
  id: string;
  name: string;
  resourceName: string;
  status: string;
  type: string;
  cpcBid: number;
}

export interface NegativeKeyword {
  id: string;
  resourceName: string;
  text: string;
  matchType: string;
  level: 'CAMPAIGN' | 'AD_GROUP';
  adGroupId?: string;
  adGroupName?: string;
}

export interface AIRecommendation {
  id: string;
  type: 'budget' | 'bidding' | 'keywords' | 'negative_keywords' | 'structure' | 'ads' | 'performance' | 'general';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  impact: string;
  action: string;
  metric?: string;
  currentValue?: string;
  targetValue?: string;
  // API action fields - populated when recommendation can be applied automatically
  campaignId?: string;
  campaignResourceName?: string;
  budgetResourceName?: string;
  suggestedBudget?: number;
  biddingStrategy?: string;
  targetCpa?: number;
  targetRoas?: number;
  keywordResourceName?: string;
  keywordText?: string;
  adGroupId?: string;
}

export interface CampaignDataForAI {
  id: string;
  name: string;
  status: string;
  type: string;
  biddingStrategy?: string;
  dailyBudget?: number;
  metrics: {
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    ctr: number;
    avgCpc: number;
    conversionValue?: number;
  };
  keywords?: CampaignKeyword[];
  negativeKeywords?: NegativeKeyword[];
}

export interface KeywordPlannerIdea {
  text: string;
  monthlySearches: number;
  competition: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNSPECIFIED';
  competitionIndex: number;
  lowBid: number;
  highBid: number;
  suggestedBid: number;
  monthlySearchVolumes?: Array<{
    year: number;
    month: string;
    monthly_searches: number;
  }>;
}

// Campaign Assets (Extensions)
export interface SitelinkAsset {
  id: string;
  name?: string;
  type: 'SITELINK';
  fieldType?: string;
  status?: string;
  sitelink: {
    linkText: string;
    description1?: string;
    description2?: string;
    finalUrls?: string[];
  };
}

export interface CalloutAsset {
  id: string;
  name?: string;
  type: 'CALLOUT';
  fieldType?: string;
  status?: string;
  callout: {
    text: string;
  };
}

export interface StructuredSnippetAsset {
  id: string;
  name?: string;
  type: 'STRUCTURED_SNIPPET';
  fieldType?: string;
  status?: string;
  structuredSnippet: {
    header: string;
    values: string[];
  };
}

export interface CallAsset {
  id: string;
  name?: string;
  type: 'CALL';
  fieldType?: string;
  status?: string;
  call: {
    phoneNumber: string;
    countryCode?: string;
  };
}

export interface CampaignAssets {
  sitelinks: SitelinkAsset[];
  callouts: CalloutAsset[];
  structuredSnippets: StructuredSnippetAsset[];
  callAssets: CallAsset[];
  all: any[];
}

// Asset Data types for create/update operations
export interface SitelinkData {
  linkText: string;
  description1?: string;
  description2?: string;
  finalUrls: string[];
}

export interface CalloutData {
  text: string;
}

export interface StructuredSnippetData {
  header: string;
  values: string[];
}

export interface CallData {
  phoneNumber: string;
  countryCode?: string;
}
